import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSubscription } from "@/hooks/use-realtime";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { Activity, CheckCircle2, XCircle, Clock, TrendingUp } from "lucide-react";

export const AutomationAnalytics = () => {
  // Fetch workflow executions
  const { data: executions, isLoading } = useQuery({
    queryKey: ["workflow-executions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("workflow_executions")
        .select(`
          *,
          automation_workflows (
            name,
            trigger_event
          )
        `)
        .order("started_at", { ascending: false })
        .limit(100);

      if (error) throw error;
      return data;
    },
  });

  // Fetch active workflows count
  const { data: workflows } = useQuery({
    queryKey: ["automation-workflows"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("automation_workflows")
        .select("id, name, is_active")
        .eq("is_active", true);

      if (error) throw error;
      return data;
    },
  });

  // Realtime subscriptions
  useRealtimeSubscription("workflow_executions", "workflow-executions");
  useRealtimeSubscription("automation_workflows", "automation-workflows");

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading analytics...</div>;
  }

  // Calculate statistics
  const totalExecutions = executions?.length || 0;
  const successfulExecutions = executions?.filter(e => e.status === 'completed').length || 0;
  const failedExecutions = executions?.filter(e => e.status === 'failed').length || 0;
  const runningExecutions = executions?.filter(e => e.status === 'running').length || 0;
  const activeWorkflows = workflows?.length || 0;

  // Prepare chart data - executions by day
  const executionsByDay = executions?.reduce((acc: any, execution) => {
    const date = new Date(execution.started_at).toLocaleDateString();
    if (!acc[date]) {
      acc[date] = { date, success: 0, failed: 0, total: 0 };
    }
    acc[date].total++;
    if (execution.status === 'completed') acc[date].success++;
    if (execution.status === 'failed') acc[date].failed++;
    return acc;
  }, {});

  const chartData = Object.values(executionsByDay || {}).slice(0, 7).reverse();

  // Status distribution for pie chart
  const statusData = [
    { name: 'Successful', value: successfulExecutions, color: 'hsl(var(--success))' },
    { name: 'Failed', value: failedExecutions, color: 'hsl(var(--destructive))' },
    { name: 'Running', value: runningExecutions, color: 'hsl(var(--warning))' },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-primary/10">
              <Activity className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Workflows</p>
              <p className="text-2xl font-bold">{activeWorkflows}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-success/10">
              <CheckCircle2 className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Successful</p>
              <p className="text-2xl font-bold">{successfulExecutions}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-destructive/10">
              <XCircle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Failed</p>
              <p className="text-2xl font-bold">{failedExecutions}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-warning/10">
              <Clock className="h-5 w-5 text-warning" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Runs</p>
              <p className="text-2xl font-bold">{totalExecutions}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Execution Trends (Last 7 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="date" className="text-xs" />
              <YAxis className="text-xs" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }} 
              />
              <Line type="monotone" dataKey="success" stroke="hsl(var(--success))" strokeWidth={2} />
              <Line type="monotone" dataKey="failed" stroke="hsl(var(--destructive))" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Status Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Recent Executions */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Executions</h3>
        <div className="space-y-4">
          {executions?.slice(0, 10).map((execution: any) => (
            <div key={execution.id} className="flex items-center justify-between p-4 rounded-lg border">
              <div className="flex-1">
                <p className="font-medium">{execution.automation_workflows?.name || 'Unknown Workflow'}</p>
                <p className="text-sm text-muted-foreground">
                  {new Date(execution.started_at).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-4">
                {execution.status === 'completed' && (
                  <div className="flex items-center gap-2 text-success">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="text-sm font-medium">Completed</span>
                  </div>
                )}
                {execution.status === 'failed' && (
                  <div className="flex items-center gap-2 text-destructive">
                    <XCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">Failed</span>
                  </div>
                )}
                {execution.status === 'running' && (
                  <div className="flex items-center gap-2 text-warning">
                    <Clock className="h-4 w-4 animate-spin" />
                    <span className="text-sm font-medium">Running</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};